import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';
import { ProjectCommunicationActions } from '@/components/project-communication-actions';
import { useAuth } from '@/contexts/auth-context';
import { getShareThumbnailCache, type CachedShareThumbnail, type ShareThumbnailDescriptor } from '@/lib/share-thumbnail-cache';
import { ApiError } from '@/lib/api-error';

export type PreviewShareRow = {
  id: number;
  title: string;
  createdAt: string;
  createdBy: number;
  createdByName: string | null;
  createdByColor?: string | null;
  projectId: string | null;
  projectName: string | null;
  thumbnail: string | null;
  urlPath: string | null;
};

type PreviewPage = { shares: PreviewShareRow[]; nextCursor: string | null };
const PAGE_SIZE = 12;
const THUMB_RETRY_MS = 110_000;
const MIN_THUMB_RETRY_MS = 5_000;

export function PreviewProjectThumb({ src, alt }: { src: string | null; alt: string }) {
  return src ? (
    <img src={src} alt={alt} className="w-12 h-6 rounded object-cover flex-shrink-0 border border-gray-600" />
  ) : (
    <div className="w-12 h-6 rounded bg-gray-700 border border-gray-600 flex-shrink-0" />
  );
}

function GeneratedThumb({ src, alt, shareId }: { src: string | null; alt: string; shareId: number }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [src]);
  if (!src || failed) return null;
  return (
    <img
      src={src}
      alt={alt}
      onError={() => setFailed(true)}
      className="w-8 h-6 rounded object-cover flex-shrink-0 border border-gray-600"
      data-testid={`latest-preview-thumbnail-${shareId}`}
    />
  );
}

type ThumbSession = {
  scope: string;
  active: boolean;
  running: boolean;
  visible: number[];
  retryAt: Map<number, number>;
  timer?: ReturnType<typeof setTimeout>;
  urls: Record<number, string>;
  epoch: number;
  cached: Map<number, CachedShareThumbnail>;
};

/**
 * One sequential batch worker per open/filter/identity session. Appending a
 * page never re-signs old entries, including shares with no generated image.
 * Only viewport entries need URLs; no originals or media generation are used.
 */
function useVisibleThumbnails(scope: string, visibleKey: string) {
  const client = useQueryClient();
  const { user } = useAuth();
  const cache = getShareThumbnailCache(client, `${user?.id ?? 'anonymous'}:${user?.role ?? ''}`);
  const sessionRef = useRef<ThumbSession | null>(null);
  const [state, setState] = useState<{
    scope: string; urls: Record<number, string>; loading: boolean; error: string | null;
  }>({ scope, urls: {}, loading: false, error: null });

  const hydrate = useCallback((session: ThumbSession) => {
    if (session.epoch !== cache.epoch) {
      session.epoch = cache.epoch;
      session.retryAt.clear();
      session.cached.clear();
      session.urls = {};
    }
    const next = { ...session.urls };
    for (const id of new Set([...session.visible, ...Object.keys(next).map(Number)])) {
      const entry = cache.read(id);
      const url = cache.url(id);
      if (url) next[id] = url;
      else delete next[id];
      if (entry && session.cached.get(id) !== entry) {
        session.cached.set(id, entry);
        session.retryAt.set(id, entry.retryAt);
      }
      if (!entry) session.cached.delete(id);
    }
    session.urls = next;
    setState(previous => ({ ...previous, scope: session.scope, urls: next }));
  }, [cache]);

  const pump = useCallback(async function pumpThumbnails(session: ThumbSession) {
    if (!session.active || session.running) return;
    hydrate(session);
    clearTimeout(session.timer);
    session.running = true;
    const processed = new Set<number>();
    try {
      while (session.active) {
        const ids = session.visible
          .filter(id => !processed.has(id) && Date.now() >= (session.retryAt.get(id) ?? -Infinity))
          .slice(0, PAGE_SIZE);
        if (!ids.length) break;
        ids.forEach(id => processed.add(id));
        setState(previous => ({ ...previous, scope: session.scope, loading: true }));
        const epoch = cache.epoch;
        try {
          const params = new URLSearchParams({ shareIds: ids.join(',') });
          const startedAt = Date.now();
          const response = await apiRequest('GET', `/api/activity-bar/preview-thumbnails?${params}`);
          const body = await response.json() as {
            thumbnails?: Array<ShareThumbnailDescriptor & { shareId: number }>;
          };
          if (!session.active) return;
          // A mutation while this request was running supersedes its result.
          if (epoch !== cache.epoch) {
            ids.forEach(id => processed.delete(id));
            continue;
          }
          const requested = new Set(ids);
          const returned = new Map<number, ShareThumbnailDescriptor>();
          for (const thumbnail of body.thumbnails ?? []) {
            if (Number.isInteger(thumbnail.shareId) && requested.has(thumbnail.shareId) && typeof thumbnail.url === 'string') {
              returned.set(thumbnail.shareId, thumbnail);
            }
          }
          ids.forEach(id => cache.put(id, returned.get(id), startedAt, epoch));
          hydrate(session);
          setState(previous => ({ ...previous, error: null }));
        } catch (error) {
          if (!session.active) return;
          if (epoch !== cache.epoch) {
            ids.forEach(id => processed.delete(id));
            continue;
          }
          if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
            ids.forEach(id => cache.put(id, undefined, Date.now(), epoch));
          }
          ids.forEach(id => session.retryAt.set(id, Date.now() + THUMB_RETRY_MS));
          setState(previous => ({
            ...previous,
            error: error instanceof Error ? error.message : 'Unable to load preview thumbnails.',
          }));
          break;
        }
      }
    } finally {
      session.running = false;
      if (session.active) {
        setState(previous => ({ ...previous, loading: false }));
        const nextAt = Math.min(...session.visible.map(id => session.retryAt.get(id) ?? Date.now()));
        if (Number.isFinite(nextAt)) {
          session.timer = setTimeout(() => void pumpThumbnails(session), Math.max(MIN_THUMB_RETRY_MS, nextAt - Date.now()));
        }
      }
    }
  }, [cache, hydrate]);

  useEffect(() => {
    const session: ThumbSession = {
      scope, active: true, running: false, visible: [], retryAt: new Map(), urls: {}, epoch: cache.epoch, cached: new Map(),
    };
    sessionRef.current = session;
    setState({ scope, urls: {}, loading: false, error: null });
    const unsubscribe = cache.subscribe(() => {
      if (!session.active) return;
      hydrate(session);
      void pump(session);
    });
    return () => {
      session.active = false;
      clearTimeout(session.timer);
      unsubscribe();
    };
  }, [scope, pump, cache, hydrate]);

  useEffect(() => {
    const session = sessionRef.current;
    if (!session || session.scope !== scope) return;
    session.visible = visibleKey ? visibleKey.split(',').map(Number) : [];
    void pump(session);
  }, [scope, visibleKey, pump]);

  const retry = () => {
    const session = sessionRef.current;
    if (!session) return;
    // Error retries are local; successful cached entries need no new request.
    session.visible.forEach(id => { session.retryAt.delete(id); session.cached.delete(id); });
    setState(previous => ({ ...previous, error: null }));
    void pump(session);
  };
  return {
    urls: state.scope === scope ? state.urls : {} as Record<number, string>,
    loading: state.scope === scope && state.loading,
    error: state.scope === scope ? state.error : null,
    retry,
  };
}

function daysAgo(iso: string) {
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  return days <= 0 ? 'today' : days === 1 ? '1 day ago' : `${days} days ago`;
}

/** Mounted only inside the open dropdown; identity changes remount it. */
export function LatestPreviews({ userId, isAdmin }: { userId: number | null; isAdmin: boolean }) {
  const [creator, setCreator] = useState<{ id: number; name: string; color?: string | null } | null>(null);
  const scroller = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState({ scope: '', key: '' });
  const query = useInfiniteQuery({
    queryKey: ['/api/activity-bar/previews', { userId, isAdmin, createdBy: creator?.id ?? null }],
    enabled: userId !== null,
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam, signal }): Promise<PreviewPage> => {
      const params = new URLSearchParams({ limit: String(PAGE_SIZE) });
      if (pageParam !== null) params.set('cursor', pageParam);
      if (isAdmin && creator) params.set('createdBy', String(creator.id));
      const response = await apiRequest('GET', `/api/activity-bar/previews?${params}`);
      const page = await response.json() as PreviewPage;
      // apiRequest owns auth-cookie recovery. Consuming the query's abort signal
      // makes closed/old-identity requests unusable even if the HTTP call finishes.
      signal.throwIfAborted();
      return page;
    },
    getNextPageParam: (page: PreviewPage) => page.nextCursor ?? undefined,
    staleTime: Infinity,
    gcTime: 0,
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  });
  const shares = useMemo(() => {
    const seen = new Set<number>();
    return (query.data?.pages.flatMap(page => page.shares) ?? [])
      .filter(share => {
        if (seen.has(share.id)) return false;
        seen.add(share.id);
        return true;
      });
  }, [query.data]);
  const scope = `${userId}:${isAdmin}:${creator?.id ?? 'all'}`;
  const thumbs = useVisibleThumbnails(scope, visible.scope === scope ? visible.key : '');

  const measureVisible = useCallback(() => {
    const element = scroller.current;
    if (!element) return;
    const viewport = element.getBoundingClientRect();
    const rows = Array.from(element.querySelectorAll<HTMLElement>('[data-preview-share-id]'));
    // A hidden/zero-size viewport must not sign every loaded page.
    const visible = viewport.height > 0
      ? rows.filter(row => {
        const bounds = row.getBoundingClientRect();
        return bounds.bottom > viewport.top && bounds.top < viewport.bottom;
      })
      : rows.slice(0, PAGE_SIZE);
    const key = visible.map(row => row.dataset.previewShareId).join(',');
    setVisible(previous => previous.scope === scope && previous.key === key ? previous : { scope, key });
  }, [scope]);

  useLayoutEffect(() => {
    if (scroller.current) scroller.current.scrollTop = 0;
    setVisible({ scope, key: '' });
  }, [scope]);
  useLayoutEffect(measureVisible, [shares, measureVisible]);
  useEffect(() => {
    window.addEventListener('resize', measureVisible);
    return () => window.removeEventListener('resize', measureVisible);
  }, [measureVisible]);

  const loadMore = () => {
    if (query.hasNextPage && !query.isFetching) void query.fetchNextPage({ cancelRefetch: false });
  };

  return (
    <div
      ref={scroller}
      className="max-h-72 overflow-y-auto"
      data-testid="latest-previews-list"
      aria-label="Latest previews"
      aria-busy={query.isFetching}
      onScroll={event => {
        measureVisible();
        const element = event.currentTarget;
        if (element.scrollHeight - element.scrollTop - element.clientHeight <= 96 && !query.isError) loadMore();
      }}
    >
      {creator && (
        <div className="flex items-center justify-between gap-2 px-1.5 pb-1.5 mb-1 border-b border-gray-700 text-xs text-gray-300">
          <span>Created by <span style={{ color: creator.color || undefined }}>{creator.name}</span></span>
          <button
            type="button"
            className="text-sky-300 hover:underline focus-visible:ring-2 focus-visible:ring-blue-400"
            onClick={() => setCreator(null)}
            aria-label="Clear creator filter"
          >Clear filter</button>
        </div>
      )}
      {query.isPending && (
        <div role="status" className="text-xs text-gray-500 px-1.5 py-2">
          {userId === null ? 'Sign in to view preview pages.' : 'Loading preview pages…'}
        </div>
      )}
      {!query.isPending && !query.isError && shares.length === 0 && (
        <div className="text-xs text-gray-500 px-1.5 py-2">No preview pages yet.</div>
      )}
      {thumbs.loading && <div role="status" className="text-[10px] text-gray-500 px-1.5 pb-1">Loading preview thumbnails…</div>}
      {shares.map(share => (
        <div key={share.id} data-preview-share-id={share.id} className="w-full flex items-center gap-2 rounded hover:bg-gray-700/60 min-w-0">
          <button
            type="button"
            onClick={() => share.urlPath && window.open(share.urlPath, '_blank', 'noopener')}
            className="flex-1 min-w-0 flex items-center gap-2 px-1.5 py-1 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
            disabled={!share.urlPath}
          >
            <PreviewProjectThumb src={share.thumbnail} alt={share.projectName ?? ''} />
            <GeneratedThumb src={thumbs.urls[share.id] ?? null} alt={`${share.title} preview thumbnail`} shareId={share.id} />
            <div className="min-w-0 flex-1">
              <div className="text-xs text-gray-200 truncate">{share.title}</div>
              <div className="text-[11px] text-gray-400 truncate">
                {share.projectName ?? ''} {share.projectId ?? ''} · {daysAgo(share.createdAt)}
                {!isAdmin && share.createdByName && <> by <span style={{ color: share.createdByColor || undefined }}>{share.createdByName}</span></>}
              </div>
            </div>
          </button>
          {isAdmin && share.createdByName && (
            <button
              type="button"
              className="text-[11px] text-sky-300 hover:underline max-w-24 truncate shrink-0 focus-visible:ring-2 focus-visible:ring-blue-400"
              style={{ color: share.createdByColor || undefined }}
              aria-label={`Filter previews by ${share.createdByName}`}
              onClick={event => {
                event.stopPropagation();
                setCreator({ id: share.createdBy, name: share.createdByName!, color: share.createdByColor });
              }}
            >{share.createdByName}</button>
          )}
          {share.projectId && (
            <ProjectCommunicationActions projectId={share.projectId} projectName={share.projectName} className="shrink-0" />
          )}
        </div>
      ))}
      {query.isError && (
        <div role="alert" className="text-xs text-red-300 px-1.5 py-2">
          {query.error instanceof Error ? query.error.message : 'Unable to load preview pages.'}
          <button
            type="button"
            className="ml-2 text-sky-300 hover:underline"
            onClick={() => void (query.isFetchNextPageError ? query.fetchNextPage() : query.refetch())}
            disabled={query.isFetching}
          >Retry loading previews</button>
        </div>
      )}
      {query.hasNextPage && !query.isError && (
        <button type="button" className="w-full text-xs text-sky-300 py-2 hover:bg-gray-700/60 rounded" disabled={query.isFetching} onClick={loadMore}>
          {query.isFetchingNextPage ? 'Loading more previews…' : 'Load more previews'}
        </button>
      )}
      {thumbs.error && (
        <div role="alert" className="text-[11px] text-red-300 px-1.5 py-1">
          {thumbs.error}
          <button type="button" className="ml-2 text-sky-300 hover:underline" onClick={thumbs.retry}>Retry preview thumbnails</button>
        </div>
      )}
    </div>
  );
}